/**
 * ⟦A-141 · C17⟧ **L'écran-titre** — premier espace du tour de validation ⟦A-140⟧, l'arrivée
 * dans le jeu.
 *
 * Verbatim de Mathieu, 09/09 : *« il faudrait un peu comme dans les jeux, tu sais, tu as un
 * menu qui te dit Nouvelle partie, Charger — c'est grisé quand tu n'as jamais eu une nouvelle
 * partie — et tu as Quitter, et tu as Paramètres. Et d'ailleurs ça peut être l'endroit où il y
 * a une sorte en fond d'une vidéo qui tourne de ce qu'est le jeu. »*
 *
 * Remplace l'ancien `ChoixDeMode` : la question « bac à sable ou compte ? » n'est plus posée
 * ici, en préalable — elle est devenue une ligne du formulaire de création (`NouvelleRun.tsx`,
 * §2 du chantier). Ce que CET écran pose, c'est une seule question, HORS jargon : as-tu une
 * partie à reprendre ?
 *
 * **Quitter, retiré ⟦C18⟧.** ⟦A-141 · C17⟧ l'avait posé conditionnel (visible seulement si une
 * run restait chargée en mémoire, venue du bouton « Menu principal », `Reglages.tsx`) faute de
 * sens littéral dans un jeu web. Décision de Mathieu, C18 : *« le supprimer — l'abandon de
 * chaîne existe déjà sous l'engrenage. »* En effet — `Reglages.tsx` porte depuis ⟦A-97 §1.2⟧ un
 * bouton « 🚪 Abandonner cette chaîne » qui clôt la run pour de bon (mémorial compris), et
 * Mathieu ne voit jamais ce bouton-ci à l'arrivée (il n'apparaissait qu'après un aller-retour
 * Réglages → Menu principal). Le double y ajoutait un second sens (« quitter » cet écran, pas
 * la partie) que rien ne distinguait clairement du premier. Revenir à cet écran-titre SANS
 * abandonner reste possible — « Menu principal » (`Reglages.tsx`) le fait déjà, run intacte,
 * « Charger » y ramène d'un clic.
 *
 * **Paramètres** — volontairement HORS routeur : `Reglages` prend un `onClose` local
 * (`Panneau.tsx`, ⟦C17⟧) plutôt que de dépendre de `sessionStore.statut === "jeu"` et d'une
 * `run` qui n'existe pas forcément ici. Plus simple, et ça évite tout flash sur « Ouverture du
 * studio… ».
 *
 * ⟦C57 · lot 6 · A-174⟧ **Moins de texte à l'écran-titre** : l'accroche (20 mots, trois phrases) devient UNE
 * phrase de 11 mots, le texte d'origine reste en infobulle (`title`) ; la confirmation « Une partie existe
 * déjà » tient en une phrase. Les Paramètres ouverts d'ici sont les Réglages (`Reglages.tsx`), allégés une fois
 * pour les deux portes.
 */

import { useState } from "react";

import { modeMemorise } from "../../game/client";
import { existeSauvegardeLocale } from "../../game/localGameClient";
import { LogoMeTube } from "../../components/LogoMeTube";
import { Reglages } from "../Reglages/Reglages";
import { useRunStore } from "../../stores/runStore";
import { useSessionStore } from "../../stores/sessionStore";
import { dialog } from "../../stores/uiStore";

import { FondMenu } from "./FondMenu";

type VueMenu = "menu" | "reglages";

export function Menu() {
  const [vue, setVue] = useState<VueMenu>("menu");
  const [enCours, setEnCours] = useState(false);
  const run = useRunStore((s) => s.run);
  const chargerPartie = useSessionStore((s) => s.chargerPartie);

  // ⟦brief §1⟧ « Charger — grisé et non cliquable s'il n'y en a pas » : une run déjà chargée en
  // mémoire (venue de « Menu principal »), une sauvegarde bac à sable sur ce navigateur, ou un
  // compte mémorisé (la seule façon de savoir si SA session tient encore est de la demander —
  // `chargerPartie()` s'en charge, et dit pourquoi si elle a expiré).
  const peutCharger = run !== null || existeSauvegardeLocale() || modeMemorise() === "compte";

  async function surNouvellePartie() {
    // Filet anti-perte : en bac à sable il n'y a qu'UNE seule sauvegarde — la lancer sans
    // prévenir écraserait silencieusement une partie qu'on vient peut-être de quitter (bouton
    // « Menu principal »), pas d'abandonner.
    if (run || existeSauvegardeLocale()) {
      const nom = run?.channelName;
      const ok = await dialog.confirm({
        title: "Une partie existe déjà",
        // ⟦C57 · lot 6⟧ Une phrase (avant : « … est toujours là. Créer une nouvelle partie en bac à
        // sable écraserait sa sauvegarde, charge-la d'abord si tu veux la garder. »).
        body: nom
          ? `Une nouvelle partie écraserait « ${nom} » : charge-la d'abord pour la garder.`
          : "Une sauvegarde t'attend : une nouvelle partie l'écraserait.",
        confirmLabel: "Créer quand même",
        cancelLabel: "Annuler",
        danger: true,
      });
      if (!ok) return;
    }
    // ⟦C17⟧ Bug trouvé en testant le parcours : si la run est encore chargée en mémoire (venue
    // du bouton « Menu principal », ⟦A-141⟧ — non destructif, exprès), `Routeur` la voit
    // toujours et retombe dans la CHAMBRE plutôt que dans le formulaire de création, quel que
    // soit `sessionStore.statut`. On débranche donc le miroir ici — la sauvegarde sur disque
    // n'est pas touchée, la confirmation ci-dessus a déjà couvert le risque d'écrasement.
    useRunStore.setState({ run: null });
    useSessionStore.setState({ statut: "jeu" });
  }

  async function surCharger() {
    if (enCours) return;
    // La run est déjà en mémoire (venue de « Menu principal ») : rien à recharger, un aller
    // simple suffit — pas d'appel réseau pour rien côté compte.
    if (run) {
      useSessionStore.setState({ statut: "jeu" });
      return;
    }
    setEnCours(true);
    try {
      await chargerPartie();
    } finally {
      setEnCours(false);
    }
  }

  if (vue === "reglages") {
    // ⟦C48 · A-168 §3⟧ Les Paramètres ouverts depuis le menu se posaient sur l'aplat jaune du
    // canevas (le seul cas où une fenêtre du jeu n'avait pas de chambre derrière elle) : le
    // même fond que l'écran-titre, sous son voile, pour que la fenêtre reste sur le décor.
    return (
      <main className="ecran-titre">
        <FondMenu />
        <div className="ecran-titre-voile" aria-hidden="true" />
        <Reglages onClose={() => setVue("menu")} />
      </main>
    );
  }

  return (
    <main className="ecran-titre">
      <FondMenu />
      <div className="ecran-titre-voile" aria-hidden="true" />
      <div className="ecran-titre-contenu">
        <h1 className="titre-jeu titre-jeu-grand">
          <LogoMeTube className="logo-metube logo-metube-grand" />
          MeTube Studio
        </h1>
        {/* ⟦C57 · lot 6⟧ Une phrase. L'accroche d'origine (« Zéro abonné. Une caméra, une chambre, et
            un million de personnes à convaincre. Au bout : une rue à ton nom. ») reste en infobulle. */}
        <p
          className="ecran-titre-accroche"
          title="Zéro abonné. Une caméra, une chambre, et un million de personnes à convaincre. Au bout : une rue à ton nom."
        >
          Zéro abonné, un million à convaincre, une rue à ton nom.
        </p>

        <nav className="menu-boutons" aria-label="Menu principal">
          <button type="button" className="bouton primaire grand" onClick={() => void surNouvellePartie()}>
            ▶ Nouvelle partie
          </button>
          <button
            type="button"
            className="bouton grand"
            disabled={!peutCharger || enCours}
            title={peutCharger ? "Reprendre la partie en cours" : "Aucune partie en cours"}
            onClick={() => void surCharger()}
          >
            {enCours ? "…" : "📂 Charger"}
          </button>
          <button type="button" className="bouton discret grand" onClick={() => setVue("reglages")}>
            ⚙ Paramètres
          </button>
        </nav>
      </div>
    </main>
  );
}
